import { DefaultRootState } from '@databases/redux';
import { Database } from '@databases/types/database';
import { DatabaseCalculationType } from '@databases/types/database/view';
import { getAllEntriesInField } from '@databases/utils/database/utils';
import { SimpleStore } from '@databases/utils/redux/redux';

import { getUniqueIdentifiers, isEmpty } from './quickCalcUtils';

export const sum = (
  database: Database,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>
): number => {
  let sum = 0;
  for (const entry of entries) {
    if (!isNaN(Number(entry.value))) {
      sum += Number(entry.value);
    }
  }
  return sum;
};

export const count = (
  database: Database,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>,
  store: SimpleStore<DefaultRootState>
): number => {
  const nonEmptyEntries = entries.filter(
    (entry) => !isEmpty(database, fieldId, entry.id)
  );
  const values: string[] = [];
  for (const entry of nonEmptyEntries) {
    const uniqueIdentifiers = getUniqueIdentifiers(database, fieldId, entry.id, store);
    for (const identifier of uniqueIdentifiers) {
      values.push(identifier.trim());
    }
  }
  return values.length;
};

export const countEmpty = (
  database: Database,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>
): number => {
  const emptyEntries = entries.filter((entry) => isEmpty(database, fieldId, entry.id));
  return emptyEntries.length;
};

export const countUnique = (
  database: Database,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>,
  store: SimpleStore<DefaultRootState>
): number => {
  const nonEmptyEntries = entries.filter(
    (entry) => !isEmpty(database, fieldId, entry.id)
  );
  const set = new Set();
  for (const entry of nonEmptyEntries) {
    const uniqueIdentifiers = getUniqueIdentifiers(database, fieldId, entry.id, store);
    for (const identifier of uniqueIdentifiers) {
      set.add(identifier);
    }
  }
  return set.size;
};

export const percentEmpty = (
  database: Database,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>
): number => {
  const emptyEntries = entries.filter((entry) => isEmpty(database, fieldId, entry.id));
  return (emptyEntries.length / entries.length) * 100;
};

export const percentNotEmpty = (
  database: Database,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>
): number => {
  const nonEmptyEntries = entries.filter(
    (entry) => !isEmpty(database, fieldId, entry.id)
  );
  return (nonEmptyEntries.length / entries.length) * 100;
};

export const average = (
  database: Database,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>
): number => {
  const nonEmptyEntries = entries.filter(
    (entry) => !isEmpty(database, fieldId, entry.id)
  );
  const _sum = sum(database, fieldId, entries);
  return _sum / nonEmptyEntries.length;
};

export const min = (
  database: Database,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>
): number => {
  const nonEmptyEntries = entries.filter(
    (entry) => !isEmpty(database, fieldId, entry.id)
  );
  if (nonEmptyEntries.length === 0) {
    return NaN;
  }
  return Math.min(...nonEmptyEntries.map((entry) => Number(entry.value)));
};

export const max = (
  database: Database,
  fieldId: string,
  entries: ReturnType<typeof getAllEntriesInField>
): number => {
  const nonEmptyEntries = entries.filter(
    (entry) => !isEmpty(database, fieldId, entry.id)
  );
  if (nonEmptyEntries.length === 0) {
    return NaN;
  }
  return Math.max(...nonEmptyEntries.map((entry) => Number(entry.value)));
};

export const calculationMap = {
  [DatabaseCalculationType.SUM]: sum,
  [DatabaseCalculationType.COUNT]: count,
  [DatabaseCalculationType.COUNT_EMPTY]: countEmpty,
  [DatabaseCalculationType.COUNT_UNIQUE]: countUnique,
  [DatabaseCalculationType.PERCENT_EMPTY]: percentEmpty,
  [DatabaseCalculationType.PERCENT_NOT_EMPTY]: percentNotEmpty,
  [DatabaseCalculationType.AVERAGE]: average,
  [DatabaseCalculationType.MIN]: min,
  [DatabaseCalculationType.MAX]: max,
};

type CalculationType = keyof typeof calculationMap;
export function getCalculationFunction<T extends CalculationType>(
  type: T
): (typeof calculationMap)[T] {
  return calculationMap[type];
}
